/**
 * Kitchen Catalog Decoder
 * Transforms abbreviated product descriptions from the Excel catalog
 * into human-readable text. All lookup tables extracted from:
 * - Excel Sheet 2 (ΚΩΔΙΚΟΙ ΤΙΜΟΚΑΤΑΛΟΓΟΥ ΚΟΥΖΙΝΑΣ v11.xlsx)
 * - PDF Catalog (Κατηγοριες.pdf)
 */

// ─── Cabinet Type (first letter of product Name field) ──────────
export const TYPE_MAP = {
  B: "Ντουλάπι Βάσεως",         // Base cabinet
  H: "Ντουλάπι Ολόσωμο",        // Tall/Full-height cabinet
  K: "Ντουλάπι Κρεμαστό",       // Wall cabinet
  L: "Ντουλάπι Κρεμαστό Βαθύ",  // Deep wall cabinet
  D: "Πάνελ Πόρτας",            // Door panel
  S: "Πλαϊνό Πάνελ",            // Side panel
}

// ─── Price Group Code → Door Material ───────────────────────────
export const CODE_MAP = {
  "1M":  { gr: "Μελαμίνη Έγχρωμη 18mm",                en: "MFC Melamine 18mm" },
  "2P":  { gr: "Μελαμίνη + PVC Edge 18mm",              en: "MFC + PVC Edge 18mm" },
  "3S":  { gr: "Synchron Effect",                        en: "Synchron Effect" },
  "4C":  { gr: "Synchron 278cm",                         en: "Synchron 278cm" },
  "5L":  { gr: "MDF Λάκα Satin 19mm",                   en: "MDF Satin Lacquer 19mm" },
  "6D":  { gr: "MDF Καπλαμάς Δρυς 20mm",                en: "MDF Veneer Oak 20mm" },
  "6L":  { gr: "MDF Λάκα Satin 19-22mm",                en: "MDF Lacquer Satin 19-22mm" },
  "7G":  { gr: "MDF Λάκα Γυαλιστερή 19mm",              en: "MDF Gloss Lacquer 19mm" },
  "7T":  { gr: "MDF Ειδικά Πλαίσια 22mm",               en: "MDF Special Frames 22mm" },
  "8D":  { gr: "Ημι-Μασίφ Δρυς 22mm",                   en: "Semi-Solid Oak 22mm" },
  "8G":  { gr: "Ημι-Μασίφ Δρυς Γυαλιστερή 22mm",       en: "Semi-Solid Oak Gloss 22mm" },
  "9H":  { gr: "Μασίφ Δρυς Faltz 22mm",                 en: "Solid Oak Faltz 22mm" },
  "9M":  { gr: "Μασίφ Δρυς 22mm",                       en: "Solid Oak 22mm" },
  "10A": { gr: "Αλουμίνιο Προφίλ + Γυαλί 20mm",        en: "Aluminum + Glass 20mm" },
}

// ─── Product Model Name → Feature Description ──────────────────
// Extracted from Sheet 2 "Name" column (123 unique model names)
export const NAME_MAP = {
  // ── Base cabinets (B prefix) ──
  "B1-P":    "1 Πόρτα",
  "B1-PA":   "1 Πόρτα Αριστερή",
  "B1-PD":   "1 Πόρτα Δεξιά",
  "B1R-P":   "1 Πόρτα + Ράφι",
  "B1S":     "1 Συρτάρι",
  "B2-P":    "2 Πόρτες",
  "B2I-BS":  "1 Καλάθι + 1 Εσωτ. Κουτί Blum",
  "B2S":     "2 Συρτάρια",
  "B2S-IK":  "2 Συρτάρια + Εσωτ. Κουτί",
  "B3S":     "3 Συρτάρια",
  "B3S-IK":  "3 Συρτάρια + Εσωτ. Κουτί",
  "B4S":     "4 Συρτάρια",
  "B5S":     "5 Συρτάρια",
  "BF-1P":   "Φούρνος 1 Πόρτα",
  "BF-2S":   "Φούρνος 2 Συρτάρια",
  "BF-2SB":  "Φούρνος 2 Συρτάρια Blum",
  "BG-AP":   "Γωνία Ανοιγόμενη Αριστερή",
  "BG-DP":   "Γωνία Ανοιγόμενη Δεξιά",
  "BG-SS":   "Γωνία Συρτάρι + Συρτάρι",
  "BN-2P":   "Νεροχύτης 2 Πόρτες",
  "BN-2P1S": "Νεροχύτης 2 Πόρτες + 1 Συρτάρι",
  "BN-A":    "Νεροχύτης Αριστερή",
  "BN-D":    "Νεροχύτης Δεξιά",
  "BN-E":    "Νεροχύτης Ευθεία",
  "BN-I":    "Νεροχύτης Εσωτερικό",
  "BP-1D":   "Πλυντήριο 1 Πόρτα Δεξιά",
  "BP-1P":   "Πλυντήριο 1 Πόρτα",
  "BS":      "Βάση Απλή",
  "BS-C":    "Βάση Γωνιακή",
  "B1-TZ":   "1 Πόρτα Τζάμι",

  // ── Tall/Full-height cabinets (H prefix) ──
  "H1-P":    "1 Πόρτα",
  "H1-PF":   "1 Πόρτα Φούρνος",
  "H2-P":    "2 Πόρτες",
  "H2-PF":   "2 Πόρτες Φούρνος",
  "H2S-1P":  "2 Συρτάρια + 1 Πόρτα",
  "H3-P":    "3 Πόρτες",
  "H4S-1P":  "4 Συρτάρια + 1 Πόρτα",
  "HF-1P":   "Φούρνος Ολόσωμο 1 Πόρτα",
  "HF-2P":   "Φούρνος Ολόσωμο 2 Πόρτες",
  "HF-3P":   "Φούρνος Ολόσωμο 3 Πόρτες",
  "HF-S":    "Φούρνος Ολόσωμο Συρτάρι",
  "HRR-G":   "Για Ψυγείο/Ψύκτη",
  "HR-1P":   "Ψυγείο 1 Πόρτα",
  "HR-2P":   "Ψυγείο 2 Πόρτες",
  "HR-G":    "Ψυγείο Γωνιακό",

  // ── Wall cabinets (K prefix) ──
  "K1-P":    "1 Πόρτα",
  "K1-PA":   "1 Πόρτα Ανοιγόμενη Πάνω",
  "K1-TZ":   "1 Πόρτα Τζάμι",
  "K2-P":    "2 Πόρτες",
  "K2-TZ":   "2 Πόρτες Τζάμι",
  "KG-1P":   "Γωνία 1 Πόρτα",
  "KG-AP":   "Γωνία Αριστερή Πόρτα",
  "KG-DP":   "Γωνία Δεξιά Πόρτα",
  "K-AN":    "Ανοιχτό (χωρίς πόρτα)",
  "KAP":     "Απορροφητήρας",

  // ── Deep wall cabinets (L prefix) ──
  "L1-P":    "Βαθύ 1 Πόρτα",
  "L1-PA":   "Βαθύ 1 Πόρτα Ανοιγόμενη",
  "L2-P":    "Βαθύ 2 Πόρτες",

  // ── Door panels (D prefix) ──
  "D-PL":    "Πλαϊνό Πάνελ",
  "D-PLN":   "Πλαϊνό Πάνελ Νέο",
  "D-EP":    "Επένδυση Πάνελ",
  "D-KR":    "Κορνίζα",
  "D-SK":    "Σκίρτινγκ",

  // ── Side panels (S prefix) ──
  "S-PL":    "Πλαϊνή Επένδυση",
  "S-PLB":   "Πλαϊνή Επένδυση Βάσεως",
  "S-PLH":   "Πλαϊνή Επένδυση Ολόσωμο",
  "S-PLK":   "Πλαϊνή Επένδυση Κρεμαστό",

  // ── Accessories & Special items ──
  "AX":      "Αξεσουάρ",
  "AX-SY":   "Αξεσουάρ Συρταριού",
  "AX-MH":   "Μηχανισμός",
  "AX-NK":   "Νεροχύτης Αξεσουάρ",
  "AX-PG":   "Πάγκος Αξεσουάρ",
  "AX-PM":   "Πόμολο",
  "AX-SB":   "Σοβατεπί",
  "AX-ST":   "Στήριξη",
  "AX-FT":   "Φωτισμός",
  "PG":      "Πάγκος",
  "PG-BK":   "Πάγκος Βακελίτης",
  "PG-LS":   "Πάγκος Λούστρο",
  "PG-PL":   "Πάγκος Πλάτη",
  "EID":     "Ειδικό",
  "EID-DK":  "Διακοσμητικό",
  "EID-EP":  "Επένδυση",
  "EID-ET":  "Εταζέρα",
  "EID-KR":  "Κορνίζα",
  "EID-RF":  "Ράφι",
  "EID-TR":  "Τραπέζι",
  "XR":      "Ειδική Χρέωση",
  "XR-KT":   "Χρέωση Κουτιού",
  "XR-PG":   "Χρέωση Πάγκου",
  "XR-PR":   "Χρέωση Πόρτας",
}

// ─── Core Material → Readable ───────────────────────────────────
export const CORE_MAP = {
  Unicolor:  "Μονόχρωμο",
  Woodgrain: "Ξύλου",
  Wood:      "Ξύλου",
}

// ─── Unit mapping (Greek → DB value) ────────────────────────────
export const UNIT_MAP = {
  "ΤΕΜΑΧΙΟ":  "Item",
  "ΤΕΜ":      "Item",
  "Μ":        "Meter",
  "M":        "Meter",
  "M2":       "CubicM",
  "Μ2":       "CubicM",
  "ΣΕΤ":      "Item",
  "SET":      "Item",
}

// ─── Category 3 normalization ───────────────────────────────────
// Excel may use Latin X ("OXI GOLA") but DB uses Greek Χ ("OΧΙ GOLA")
export const normalizeCat3 = (val) => {
  if (!val) return val
  // Normalize "OXI GOLA" → "OΧΙ GOLA" (Latin X → Greek Χ)
  return val.replace(/^OXI\s/i, "OΧΙ ")
}

/**
 * Generate a human-readable description from an Excel row.
 *
 * Input (Excel row object with keys matching Sheet 7 columns):
 *   Name: "B2I-BS"
 *   Description: "ΝΤ.Β.ΜΕΛ.ΕΓ. 120X72 ΠΟΡ.PRFS.ΛΑΚ.ΓΥΑΛ. 1ΚΑΛ.1ΕΣ.Κ.BL"
 *   Code: "10A"
 *   Category2: "ΒΑΣΕΩΣ"
 *   Category3: "GOLA"
 *   CoreMaterial: "Unicolor"
 *   Dimension1: 1200   (width mm)
 *   Dimension2: 720    (depth mm)
 *   Dimension3: 600    (height mm)
 *
 * Output:
 *   "Ντουλάπι Βάσεως — 1200×720mm — GOLA — Πόρτα: Αλουμίνιο Προφίλ + Γυαλί 20mm — 1 Καλάθι + 1 Εσωτ. Κουτί Blum — Μονόχρωμο"
 */
export const generateDescription = (row) => {
  const parts = []

  // 1. Cabinet type from first letter of Name
  const name = row.Name || row.name || ""
  const firstChar = name.charAt(0).toUpperCase()
  const cabinetType = TYPE_MAP[firstChar]
  if (cabinetType) parts.push(cabinetType)

  // 2. Dimensions (width × depth mm)
  const dim1 = row.Dimension1 || row.dimension1
  const dim2 = row.Dimension2 || row.dimension2
  if (dim1) {
    const dimStr = dim2 ? `${dim1}×${dim2}mm` : `${dim1}mm`
    parts.push(dimStr)
  }

  // 3. GOLA / OΧΙ GOLA
  const cat3 = row.Category3 || row.category3
  if (cat3 && (cat3.includes("GOLA") || cat3.includes("gola"))) {
    parts.push(normalizeCat3(cat3))
  }

  // 4. Door material from price group code
  const pgCode = row.Code || row.code
  const pgInfo = CODE_MAP[pgCode]
  if (pgInfo) {
    parts.push(`Πόρτα: ${pgInfo.gr}`)
  }

  // 5. Features from model name
  const features = NAME_MAP[name]
  if (features) {
    parts.push(features)
  }

  // 6. Core material type
  const core = row.CoreMaterial || row.coreMaterial
  const coreLabel = CORE_MAP[core]
  if (coreLabel) {
    parts.push(coreLabel)
  }

  return parts.join(" — ") || row.Description || row.description || name
}

/**
 * Build the 6-level category array from an Excel row.
 * Format: [cat1, cat2, cat3, cat4, priceGroup, coreMaterial]
 */
export const buildCategoryArray = (row) => {
  const arr = [
    row.Category1 || "",
    row.Category2 || "",
    normalizeCat3(row.Category3 || ""),
    row.Category4 || "",
    row.Code || "",
    row.CoreMaterial || "",
  ].filter(Boolean)
  return arr
}

/**
 * Map an Excel row to the DB product PATCH payload.
 * The `existingId` is the product's DB id (required for PATCH).
 */
export const mapExcelToProduct = (row, existingId) => {
  const unit = UNIT_MAP[row["Μ.Μ."] || row.Unit || ""] || "Item"

  return {
    id: existingId,
    description: generateDescription(row),
    provsku: row["Ονομασία Είδους"] || row.Description || "",
    category: JSON.stringify(buildCategoryArray(row)),
    b2bsalesprice: row.StandardPrice || row.standardPrice || null,
    salesprice: row.DealerPrice || row.dealerPrice || null,
    productDepth: row.Dimension1 || null,
    productWidth: row.Dimension2 || null,
    productHeight: row.Dimension3 || null,
    unit,
    vat: 24,
    usageType: "COMPLETE-PRODUCT",
  }
}
